import { getEpcisThunk } from '@redux/features/epcis.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import FormModal from 'Common/Components/Modal/FormModal';
import OutboundValidationPanel from 'components/integrations/OutboundValidationPanel';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DebouncedInput from 'Shared/DebouncedInput';
import FilterInput from 'Shared/FilterInput';
import epcisService from 'services/api/epcis.service';
import ViewPdfLink from 'components/ui/ViewPdfLink';
import { isPdfPath, resolveUploadFileUrl } from 'helpers/resolveUploadFileUrl';
import { toast } from 'react-toastify';

function formatBizStep(status?: string | null): string {
    if (!status) return '—';
    return status.replace(/_/g, ' ');
}

function isShippingDocument(status?: string | null): boolean {
    if (!status) return false;
    return status.toLowerCase().includes('shipping');
}

const Epcis: FC = () => {
    // -----------------------------------------------------------------
    const [page, setPage] = useState<number>(1);
    const [email, setEmail] = useState<string>('');
    const [ndcCode, setNdcCode] = useState<string>('');
    const [lotNumber, setLotNumber] = useState<string>('');
    const [itemName, setItemName] = useState<string>('');
    const [expiredOnly, setExpiredOnly] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [voidModalOpen, setVoidModalOpen] = useState(false);
    const [voidTargetId, setVoidTargetId] = useState<number | null>(null);
    const [voidEventId, setVoidEventId] = useState('');
    const [voidEpcUrn, setVoidEpcUrn] = useState('');
    const [voidLoading, setVoidLoading] = useState(false);
    const [partialVoidModalOpen, setPartialVoidModalOpen] = useState(false);
    const [partialVoidTargetId, setPartialVoidTargetId] = useState<number | null>(null);
    const [partialVoidEventId, setPartialVoidEventId] = useState('');
    const [partialVoidEpcUrns, setPartialVoidEpcUrns] = useState('');
    const [partialVoidLoading, setPartialVoidLoading] = useState(false);
    const [validationModalOpen, setValidationModalOpen] = useState(false);
    const [validationTargetId, setValidationTargetId] = useState<number | null>(null);
    // -----------------------------------------------------------------

    const dispatch = useAppDispatch();
    const { epcis, loading } = useAppSelector((state: RootState) => state.epcisState);
    // -----------------------------------------------------------------

    const changePage = (p: string) => setPage(Number(p));
    // -----------------------------------------------------------------

    // Helper function to format date to YYYY-MM-DDTHH:MM:SS format
    const formatDateForAPI = (date: Date, isEndDate: boolean = false): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const time = isEndDate ? '23:59:59' : '00:00:00';
        return `${year}-${month}-${day}T${time}`;
    };

    // Handle date range changes - only send when both dates are selected
    const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
        setDateRange(dates);
        const [start, end] = dates;
        if (start && end) {
            setStartDate(formatDateForAPI(start, false));
            setEndDate(formatDateForAPI(end, true));
        } else if (!start && !end) {
            setStartDate('');
            setEndDate('');
        }
    };

    // -----------------------------------------------------------------

    useEffect(() => {
        dispatch(
            getEpcisThunk({
                size: 10,
                page: page - 1,
                email: email || '',
                ndcCode: ndcCode || '',
                lotNumber: lotNumber || '',
                itemName: itemName || '',
                expiredOnly: expiredOnly === 'true' ? true : expiredOnly === 'false' ? false : false,
                startDate: startDate || '',
                endDate: endDate || '',
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, email, ndcCode, lotNumber, itemName, expiredOnly, startDate, endDate]);
    // -----------------------------------------------------------------

    const downloadEpcisJson = async (epcisId: number) => {
        if (!epcisId) return;
        try {
            const response = await epcisService.downloadEpcisJsonById(epcisId);
            const blob = new Blob([response.data], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `epcis_${epcisId}_export_2_0.json`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                'Failed to export EPCIS JSON (2.0)';
            alert(message);
        }
    };

    const openVoidShipping = (epcisId: number) => {
        setVoidTargetId(epcisId);
        setVoidEventId('');
        setVoidEpcUrn('');
        setVoidModalOpen(true);
    };

    const openPartialVoidShipping = (epcisId: number) => {
        setPartialVoidTargetId(epcisId);
        setPartialVoidEventId('');
        setPartialVoidEpcUrns('');
        setPartialVoidModalOpen(true);
    };

    const openValidation = (epcisId: number) => {
        setValidationTargetId(epcisId);
        setValidationModalOpen(true);
    };

    const submitVoidShipping = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!voidTargetId || !voidEventId.trim() || !voidEpcUrn.trim()) {
            toast.error('Original shipping event ID and EPC URN are required');
            return;
        }
        setVoidLoading(true);
        try {
            await epcisService.voidShipping(voidTargetId, voidEventId.trim(), voidEpcUrn.trim());
            toast.success('Void-shipping event appended to document');
            setVoidModalOpen(false);
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                'Failed to append void-shipping event';
            toast.error(message);
        } finally {
            setVoidLoading(false);
        }
    };

    const submitPartialVoidShipping = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!partialVoidTargetId || !partialVoidEventId.trim()) {
            toast.error('Original shipping event ID is required');
            return;
        }
        const epcUrns = partialVoidEpcUrns
            .split(/[\n,]+/)
            .map((urn) => urn.trim())
            .filter(Boolean);
        if (epcUrns.length === 0) {
            toast.error('At least one EPC URN is required');
            return;
        }
        setPartialVoidLoading(true);
        try {
            await epcisService.partialVoidShipping(partialVoidTargetId, partialVoidEventId.trim(), epcUrns);
            toast.success(`Partial void appended for ${epcUrns.length} EPC(s)`);
            setPartialVoidModalOpen(false);
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                'Failed to append partial void-shipping events';
            toast.error(message);
        } finally {
            setPartialVoidLoading(false);
        }
    };

    const downloadEpcisXml = async (epcisId: number, version: '1.0' | '1.2' | '2.0') => {
        if (!epcisId) return;
        try {
            const response = await epcisService.downloadEpcisXmlById(epcisId, version);
            const blob = new Blob([response.data], { type: 'application/xml' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `epcis_${epcisId}_export_${version.replace('.', '_')}.xml`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                `Failed to export EPCIS XML (${version})`;
            alert(message);
        }
    };

    const renderPathCell = (path: string | null | undefined, documentId?: string | null) => {
        if (!path || path === '-') {
            return <span className="text-gray-500">-</span>;
        }

        if (isPdfPath(path)) {
            return <ViewPdfLink path={path} documentId={documentId} />;
        }

        const href = resolveUploadFileUrl(path);
        return (
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
            >
                {path}
            </a>
        );
    };

    // -----------------------------------------------------------------

    return (
        <>
            <BreadCrumb pageTitle="EPCIS" />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">EPCIS List</h3>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-5 flex-wrap">
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Email</label>
                                <DebouncedInput placeholder="Search by email" searchValue={email} searchFn={setEmail} />
                            </div>
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">NDC Code</label>
                                <DebouncedInput
                                    placeholder="Search NDC code"
                                    searchValue={ndcCode}
                                    searchFn={setNdcCode}
                                />
                            </div>
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Lot Number</label>
                                <DebouncedInput
                                    placeholder="Search lot number"
                                    searchValue={lotNumber}
                                    searchFn={setLotNumber}
                                />
                            </div>
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Item Name</label>
                                <DebouncedInput
                                    placeholder="Search item name"
                                    searchValue={itemName}
                                    searchFn={setItemName}
                                />
                            </div>
                            <div className="flex-1 min-w-[220px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Expired Only</label>
                                <FilterInput
                                    value={expiredOnly}
                                    onChange={(v) => setExpiredOnly(v)}
                                    options={[
                                        { value: '', label: 'All' },
                                        { value: 'true', label: 'True' },
                                        { value: 'false', label: 'False' },
                                    ]}
                                    id="expiredOnly"
                                    placeholder="All"
                                    optionLabelKey="label"
                                    optionValueKey="value"
                                />
                            </div>
                            <div className="flex-1 min-w-[320px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Date Range</label>
                                <DatePicker
                                    selected={dateRange[0]}
                                    onChange={handleDateRangeChange}
                                    startDate={dateRange[0]}
                                    endDate={dateRange[1]}
                                    selectsRange
                                    isClearable
                                    placeholderText="Select date range"
                                    className="form-input border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                                    dateFormat="yyyy-MM-dd"
                                />
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Document ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Email</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Biz step</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Partner GLN</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Path</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    product Batch Id
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    sales Order Item Id
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Source</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Validation</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Export</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={(epcis as any)?.content?.length ?? 0} animationKey={`epcis-${page}-${email}-${ndcCode}-${lotNumber}-${startDate}-${endDate}`}>
                                            {(epcis as any)?.content?.map((item: any) => (
                                                <AnimatedTableRow
                                                    rowKey={item?.id}
                                                    detailRecord={item}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{item?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.documentId || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.email || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{formatBizStep(item?.eventStatus)}</td>
                                                    <td className="px-3.5 py-2.5">{item?.partnerGln || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {renderPathCell(item?.path, item?.documentId)}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{item?.productBatchId || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.salesOrderItemId || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <span className="text-xs text-slate-600">
                                                            JSON {item?.sourceSchemaVersion || '2.0'}
                                                            {item?.jsonPathPresent ? ' (file)' : ' (pdf/legacy)'}
                                                        </span>
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        <span
                                                            className={
                                                                item?.validationStatus === 'Export ready'
                                                                    ? 'text-xs font-medium text-green-700'
                                                                    : 'text-xs text-slate-600'
                                                            }
                                                        >
                                                            {item?.validationStatus || '—'}
                                                        </span>
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {item?.id ? (
                                                            <div className="flex flex-col gap-1">
                                                                <button
                                                                    type="button"
                                                                    title="Export canonical EPCIS 2.0 JSON-LD document"
                                                                    className="text-yellow-600 hover:text-yellow-800 hover:underline font-semibold text-left text-sm"
                                                                    onClick={() => downloadEpcisJson(item.id)}
                                                                >
                                                                    Export JSON (2.0)
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title="Stored as JSON 2.0; exports as EPCIS 1.0 XML"
                                                                    className="text-yellow-600 hover:text-yellow-800 hover:underline font-semibold text-left text-sm"
                                                                    onClick={() => downloadEpcisXml(item.id, '1.0')}
                                                                >
                                                                    Export XML (1.0)
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title="Stored as JSON 2.0; exports as EPCIS 1.2 XML"
                                                                    className="text-yellow-600 hover:text-yellow-800 hover:underline font-semibold text-left text-sm"
                                                                    onClick={() => downloadEpcisXml(item.id, '1.2')}
                                                                >
                                                                    Export XML (1.2)
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title="Stored as JSON 2.0; exports as EPCIS 2.0 XML"
                                                                    className="text-yellow-600 hover:text-yellow-800 hover:underline font-semibold text-left text-sm"
                                                                    onClick={() => downloadEpcisXml(item.id, '2.0')}
                                                                >
                                                                    Export XML (2.0)
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-500">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {item?.id ? (
                                                            <div className="flex flex-col gap-1">
                                                                {isShippingDocument(item?.eventStatus) ? (
                                                                    <button
                                                                        type="button"
                                                                        className="text-sm font-semibold text-[#bd902e] hover:underline text-left"
                                                                        onClick={() => openValidation(item.id)}
                                                                    >
                                                                        L4 validation
                                                                    </button>
                                                                ) : null}
                                                                {isShippingDocument(item?.eventStatus) ? (
                                                                    <button
                                                                        type="button"
                                                                        className="text-sm font-semibold text-red-700 hover:text-red-900 hover:underline text-left"
                                                                        onClick={() => openVoidShipping(item.id)}
                                                                    >
                                                                        Void shipping
                                                                    </button>
                                                                ) : null}
                                                                {isShippingDocument(item?.eventStatus) ? (
                                                                    <button
                                                                        type="button"
                                                                        className="text-sm font-semibold text-orange-700 hover:text-orange-900 hover:underline text-left"
                                                                        onClick={() => openPartialVoidShipping(item.id)}
                                                                    >
                                                                        Partial void (§14.9.4)
                                                                    </button>
                                                                ) : null}
                                                                {!isShippingDocument(item?.eventStatus) ? (
                                                                    <span className="text-gray-500">—</span>
                                                                ) : null}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-500">—</span>
                                                        )}
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={(epcis as any)?.pageable?.pageSize || 10}
                                    total={(epcis as any)?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={(epcis as any)?.numberOfElements || (epcis as any)?.content?.length || 0}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>

            <FormModal
                show={voidModalOpen}
                onHide={() => setVoidModalOpen(false)}
                title="Void shipping event (R1.3 §10.12)"
                onSubmit={submitVoidShipping}
                footer={
                    <>
                        <ButtonBase
                            innerText="Cancel"
                            className="rounded border-none bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                            onClick={() => setVoidModalOpen(false)}
                            disabled={voidLoading}
                        />
                        <ButtonBase
                            innerText={voidLoading ? 'Saving…' : 'Append void event'}
                            className="rounded border-none bg-yellow-600 px-4 py-2 text-white hover:bg-yellow-800 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={submitVoidShipping}
                            disabled={voidLoading}
                        />
                    </>
                }
            >
                <p className="mb-4 text-sm text-slate-600">
                    Appends an ObjectEvent with errorDeclaration referencing the original shipping event. Use the
                    event ID and serialized EPC URN from the shipping document.
                </p>
                <div className="flex flex-col gap-4">
                    <TextField
                        fullWidth
                        size="small"
                        label="Original shipping event ID"
                        value={voidEventId}
                        onChange={(e) => setVoidEventId(e.target.value)}
                        placeholder="urn:uuid:…"
                    />
                    <TextField
                        fullWidth
                        size="small"
                        label="EPC URN"
                        value={voidEpcUrn}
                        onChange={(e) => setVoidEpcUrn(e.target.value)}
                        placeholder="urn:epc:id:sgtin:…"
                    />
                </div>
            </FormModal>

            <FormModal
                show={partialVoidModalOpen}
                onHide={() => setPartialVoidModalOpen(false)}
                title="Partial void shipping (§14.9.4)"
                onSubmit={submitPartialVoidShipping}
                footer={
                    <>
                        <ButtonBase
                            innerText="Cancel"
                            className="rounded border-none bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                            onClick={() => setPartialVoidModalOpen(false)}
                            disabled={partialVoidLoading}
                        />
                        <ButtonBase
                            innerText={partialVoidLoading ? 'Saving…' : 'Append partial void'}
                            className="rounded border-none bg-orange-600 px-4 py-2 text-white hover:bg-orange-800 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={submitPartialVoidShipping}
                            disabled={partialVoidLoading}
                        />
                    </>
                }
            >
                <p className="mb-4 text-sm text-slate-600">
                    Voids a subset of serialized EPCs from a shipping event. Enter one EPC URN per line (or
                    comma-separated).
                </p>
                <div className="flex flex-col gap-4">
                    <TextField
                        fullWidth
                        size="small"
                        label="Original shipping event ID"
                        value={partialVoidEventId}
                        onChange={(e) => setPartialVoidEventId(e.target.value)}
                        placeholder="urn:uuid:…"
                    />
                    <TextField
                        fullWidth
                        size="small"
                        label="EPC URNs to void"
                        value={partialVoidEpcUrns}
                        onChange={(e) => setPartialVoidEpcUrns(e.target.value)}
                        placeholder="urn:epc:id:sgtin:…"
                        multiline
                        minRows={4}
                    />
                </div>
            </FormModal>

            <FormModal
                show={validationModalOpen}
                onHide={() => setValidationModalOpen(false)}
                title="Outbound L4 validation"
                footer={
                    <ButtonBase
                        innerText="Close"
                        className="rounded border-none bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                        onClick={() => setValidationModalOpen(false)}
                    />
                }
            >
                {validationTargetId ? (
                    <OutboundValidationPanel epcisId={validationTargetId} />
                ) : null}
            </FormModal>
        </>
    );
};

export default Epcis;
